import { useEffect, useState } from "react";
import clsx from "clsx";

type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

/**
 * Read the user's preferred theme:
 *   1. Explicit localStorage choice wins.
 *   2. Otherwise system prefers-color-scheme.
 *   3. Default light.
 *
 * Kept as a free function so the initial paint can call it synchronously
 * (avoids the "flash of wrong theme" you get if you set it from inside
 * an effect).
 */
function readInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

/**
 * Apply the theme to <html> by toggling the `dark` class — Tailwind's `darkMode: "class"`
 * config in tailwind.config.ts keys off this.
 */
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

interface DarkModeToggleProps {
  className?: string;
}

export default function DarkModeToggle({ className }: DarkModeToggleProps) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  // Apply on mount and whenever theme changes.
  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const next: Theme = theme === "dark" ? "light" : "dark";
  const label = theme === "dark" ? "라이트 모드로 전환" : "다크 모드로 전환";

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => setTheme(next)}
      className={clsx(
        // 모바일 44px, 데스크톱 36px (RefreshIndicator 와 동일 규칙).
        "inline-flex h-11 w-11 items-center justify-center rounded-full border transition sm:h-9 sm:w-9",
        "border-surface-border bg-surface text-slate-600 hover:bg-slate-100",
        "dark:border-surface-darkBorder dark:bg-surface-darkSubtle dark:text-slate-300 dark:hover:bg-slate-800",
        className,
      )}
    >
      {theme === "dark" ? (
        // Sun icon — clicking switches to light.
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        // Moon icon — clicking switches to dark.
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}
